import {
  computed,
  type ComputedRef,
  hasInjectionContext,
  inject,
  type InjectionKey,
  type MaybeRefOrGetter,
  provide,
  toValue,
  warn,
} from 'vue';

import type { Brand, Organization, PermissionObject } from '@/types/auth.types';

import { useIdentityStore } from '@/stores/identity';

const BRAND_SCOPE: InjectionKey<ComputedRef<Brand | null>> = Symbol('brandScope');

/**
 * Scopes this component's subtree to `brandId`, so descendants' {@link useActiveBrand} resolve to
 * it. `null` when the user has no access to the brand: render an access state, never fall back to
 * the current brand (docs/decisions/brand-scope.md). A nullish `brandId` keeps the outer brand,
 * for roots like registry drawers whose brand is optional.
 */
export function provideBrandScope(
  brandId: MaybeRefOrGetter<number | null | undefined>,
): ComputedRef<Brand | null> {
  const identityStore = useIdentityStore();
  const outer = useActiveBrand();
  const brand = computed(() => {
    const id = toValue(brandId);
    return id == null ? outer.value : (identityStore.identityBrandsById[id] ?? null);
  });
  provide(BRAND_SCOPE, brand);
  return brand;
}

/**
 * The brand the calling component operates on: the nearest {@link provideBrandScope}, else the
 * current brand. Read brand data through this instead of `identityStore.currentBrand`.
 * Singletons (stores, `defineQuery`) have no component tree, so they always see the current brand.
 */
export function useActiveBrand(): ComputedRef<Brand | null> {
  if (!hasInjectionContext()) {
    // No tree to inject from, so a caller under a scope would silently get the current brand.
    warn('useActiveBrand() called outside setup; brand scope ignored.');
    return useCurrentBrand();
  }
  return inject(BRAND_SCOPE, null) ?? useCurrentBrand();
}

function useCurrentBrand(): ComputedRef<Brand | null> {
  const identityStore = useIdentityStore();
  return computed(() => identityStore.currentBrand);
}

/**
 * Id of {@link useActiveBrand}, or `null` before identity loads. A non-null `explicitBrandId`
 * wins, for callers handed a brand by their consumer.
 */
export function useActiveBrandId(
  explicitBrandId?: MaybeRefOrGetter<number | null | undefined>,
): ComputedRef<number | null> {
  const activeBrand = useActiveBrand();
  return computed(() => toValue(explicitBrandId) ?? activeBrand.value?.id ?? null);
}

/**
 * Fills a missing `brandId` in mutation vars with the caller's {@link useActiveBrandId}.
 * `defineMutation` singletons can't inject, so the calling component resolves the brand.
 */
export function useWithActiveBrandId() {
  const activeBrandId = useActiveBrandId();
  return <V extends { brandId?: number | null }>(vars: V) => ({
    ...vars,
    brandId: (vars.brandId ?? activeBrandId.value)!,
  });
}

/** Organization of {@link useActiveBrand}, not of the globally selected brand. */
export function useActiveOrganization(): ComputedRef<Organization | null> {
  const activeBrand = useActiveBrand();
  return computed(() => activeBrand.value?.organization ?? null);
}

/**
 * `identityStore.guard` against {@link useActiveBrand}. Under a scope on an inaccessible brand it
 * denies, where `guard(permission, null)` would fall back to the current brand.
 */
export function useBrandGuard() {
  const identityStore = useIdentityStore();
  const scope = hasInjectionContext() ? inject(BRAND_SCOPE, null) : null;
  return (permission: PermissionObject | PermissionObject[]): boolean => {
    if (!scope) return identityStore.guard(permission);
    return scope.value ? identityStore.guard(permission, scope.value) : false;
  };
}
